import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    businessType: "",
    enquiryType: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.enquiryType) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/contact`, formData);
      
      // Track form submission
      if (window.trackContactFormSubmitted) {
        window.trackContactFormSubmitted("contact_form", "contact_page");
      }

      if (formData.enquiryType === "broker_call") {
        if (window.trackBrokerCallRequested) {
          window.trackBrokerCallRequested();
        }
      }
      
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneClick = () => {
    if (window.trackPhoneClick) {
      window.trackPhoneClick("1300 266 922");
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your enquiry has been received. One of our ecommerce business specialists will be in touch within 1 business day.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/valuation")}
                className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              >
                Start Your Valuation Guide
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="w-full"
              >
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  Speak With an Ecommerce Broker
                </h1>
                <p className="text-slate-600 mb-8">
                  Complete the form below and one of our ecommerce business specialists will be in touch.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Your name"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="your@email.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="04XX XXX XXX"
                        data-testid="contact-phone"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="businessType" className="text-slate-700">
                        Business Type
                      </Label>
                      <Select
                        value={formData.businessType}
                        onValueChange={(value) => handleChange("businessType", value)}
                      >
                        <SelectTrigger data-testid="contact-business-type">
                          <SelectValue placeholder="Select your business type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shopify">Shopify Store</SelectItem>
                          <SelectItem value="amazon">Amazon Business</SelectItem>
                          <SelectItem value="ebay">eBay Store</SelectItem>
                          <SelectItem value="dtc">Direct-to-Consumer Brand</SelectItem>
                          <SelectItem value="subscription">Subscription Ecommerce</SelectItem>
                          <SelectItem value="dropshipping">Dropshipping Business</SelectItem>
                          <SelectItem value="wholesale">Wholesale & Ecommerce</SelectItem>
                          <SelectItem value="marketplace">Marketplace Business</SelectItem>
                          <SelectItem value="multichannel">Multi-Channel Ecommerce</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="enquiryType" className="text-slate-700">
                      Enquiry Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.enquiryType}
                      onValueChange={(value) => handleChange("enquiryType", value)}
                    >
                      <SelectTrigger data-testid="contact-enquiry-type">
                        <SelectValue placeholder="What can we help you with?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="valuation">Business Valuation Question</SelectItem>
                        <SelectItem value="broker_call">Book a Confidential Broker Call</SelectItem>
                        <SelectItem value="selling">Ready to Sell My Business</SelectItem>
                        <SelectItem value="general">General Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Tell us a bit about your business and how we can help..."
                      rows={5}
                      data-testid="contact-message"
                    />
                  </div>

                  <p className="text-sm text-slate-500">
                    All enquiries are treated as confidential. We will not share your information with third parties.
                  </p>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full md:w-auto bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-8 py-3 rounded-full font-semibold"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Enquiry
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Contact Details</h3>
                  <div className="space-y-4">
                    <a
                      href="tel:1300266922"
                      onClick={handlePhoneClick}
                      className="flex items-center gap-3 text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Phone</p>
                        <p className="font-medium">1300 266 922</p>
                      </div>
                    </a>
                    
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Service Area</p>
                        <p className="font-medium">Australia-wide</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-slate-600">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Response Time</p>
                        <p className="font-medium">Within 1 business day</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Prefer to Start Online?</h3>
                  <p className="text-slate-600 mb-4">
                    Get an instant indicative valuation for your ecommerce business.
                  </p>
                  <Button
                    onClick={() => {
                      if (window.trackValuationStarted) {
                        window.trackValuationStarted("contact-sidebar");
                      }
                      navigate("/valuation");
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
                  >
                    Start Your Valuation Guide
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 text-white">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400 mb-3">Powered by</p>
                  <img src={BONZA_LOGO} alt="Bonza Business & Franchise Sales" className="h-8 mb-4" />
                  <p className="text-sm text-slate-400">
                    Bonza Business & Franchise Sales provides the brokerage expertise and sales support behind the platform.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-8 brightness-0 invert" />
              <span className="text-sm">Powered by Bonza Business & Franchise Sales</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
